import { useEffect, useState } from "react";

import { PACKS } from "../components/Form";
import { sendPostRequest, formatPhoneNumber } from "../utils";

export const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
export const POSTAL_CODE_PATTERN = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/;
export const PHONE_ERROR = "Numéro de téléphone invalide";
export const GENERIC_ERROR =
  "Une erreur est survenue. Veuillez réessayer dans un instant.";

export const EMPTY_SIGNUP = {
  city: "",
  name: "",
  phone_number: "",
  postal_code: "",
  quantity: PACKS[0].value,
  email: "",
  address: "",
  number: "",
  apartment: "",
  accept_sms_notification: false,
  accept_email_notification: false,
};

// Same rule as the current form: every required field, a valid email, a
// valid Canadian postal code and the SMS consent. Apartment and the
// newsletter consent are optional.
export const isSignupComplete = (inputs, phoneNumberError) => {
  const isValidPostalCode = inputs.postal_code
    ? POSTAL_CODE_PATTERN.test(inputs.postal_code)
    : true;
  return Boolean(
    inputs.city &&
      inputs.name &&
      inputs.phone_number &&
      inputs.postal_code &&
      inputs.quantity &&
      EMAIL_PATTERN.test(inputs.email) &&
      inputs.address &&
      inputs.accept_sms_notification &&
      isValidPostalCode &&
      !phoneNumberError
  );
};

// Signup form state and the subscribe → checkout → Stripe flow, shared by
// the current home page and the new one
export const useSignup = () => {
  const [inputs, setInputs] = useState(EMPTY_SIGNUP);
  const [error, setError] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pressing Back on Stripe's page can bring this page back exactly as it
  // was left, button still disabled; make it usable again
  useEffect(() => {
    const onPageShow = (event) => {
      if (event.persisted) setIsSubmitting(false);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const handleChange = (event) => {
    const name = event.target.name;
    const value =
      name === "accept_sms_notification" || name === "accept_email_notification"
        ? event.target.checked
        : event.target.value;

    if (name === "phone_number") {
      if (value && !/^[0-9]+$/.test(value)) return;
      if (!/^\d{10}$/.test(Number(value)) && !phoneNumberError) {
        setPhoneNumberError(PHONE_ERROR);
      } else if (/^\d{10}$/.test(Number(value)) && phoneNumberError) {
        setPhoneNumberError("");
      }
    }

    setInputs((values) => ({ ...values, [name]: value }));
  };

  const fail = (message) => {
    setError(message || GENERIC_ERROR);
    setIsSubmitting(false);
  };

  const doCheckOut = async (userId) => {
    const res = await sendPostRequest("create-checkout-session", {
      lookup_key: String(inputs.quantity),
      user_info: {
        ...inputs,
        phone_number: formatPhoneNumber(inputs.phone_number),
      },
      userId,
    });
    const body = await res.json();
    if (body?.url) {
      window.location.href = body.url;
      return;
    }
    fail(body?.errMessage);
  };

  const submitForm = (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);
    return sendPostRequest("subscribe", {
      ...inputs,
      phone_number: formatPhoneNumber(inputs.phone_number),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data?.userId) return doCheckOut(data.userId);
        fail(data?.errMessage);
      })
      .catch(() => fail());
  };

  return {
    inputs,
    error,
    phoneNumberError,
    isSubmitting,
    isComplete: isSignupComplete(inputs, phoneNumberError),
    handleChange,
    submitForm,
  };
};
